import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  ScrollView,
  Alert,
  ActivityIndicator,
  Modal,
  Platform
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import { collection, updateDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { useAuth } from '../private/AuthContext';
import CampayPaymentForm from '../services/CampayPaymentForm';

const SUBSCRIPTION_PLANS = [
  {
    id: 'five_days',
    name: '5 Jours',
    price: 15000,
    days: 5,
    color: '#4CAF50'
  },
  {
    id: 'fifteen_days',
    name: '15 Jours',
    price: 35000,
    days: 15,
    color: '#2196F3'
  },
  {
    id: 'thirty_days',
    name: '30 Jours',
    price: 100000,
    days: 30,
    color: '#9C27B0'
  },
  {
    id: 'three_months',
    name: '3 Mois',
    price: 300000,
    days: 90,
    color: '#FF9800'
  }
];

const paymentMethods = [
  {
    id: 'orange',
    name: 'Orange Money',
    icon: 'cellphone',
    color: '#FF6F00',
  },
  {
    id: 'mtn',
    name: 'Mobile Money',
    icon: 'cellphone',
    color: '#FFCA28',
  },
];

// Ajoute la fonction showAlert compatible web/mobile
const showAlert = (title, message, buttons = null) => {
  if (Platform.OS === 'web') {
    if (buttons && buttons.length > 0) {
      const confirmed = window.confirm(`${title}\n\n${message}`);
      if (confirmed && buttons[0].onPress) {
        buttons[0].onPress();
      }
    } else {
      window.alert(`${title}\n\n${message}`);
    }
  } else {
    if (buttons) {
      Alert.alert(title, message, buttons);
    } else {
      Alert.alert(title, message);
    }
  }
};

export default function RenewAnnouncementScreen({ route, navigation }) {
  const { announcement, previousPlan } = route.params;
  const { theme } = useTheme();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [showCampayForm, setShowCampayForm] = useState(false);

  const handleRenewalSuccess = async (paymentData) => {
    try {
      setLoading(true);
      
      // Calculer la nouvelle date d'expiration
      const newExpirationDate = new Date();
      if (announcement.subscriptionPlan?.expirationDate) {
        const currentExpiration = new Date(announcement.subscriptionPlan.expirationDate);
        if (currentExpiration > newExpirationDate) {
          // Si l'annonce n'est pas encore expirée, ajouter les jours à partir de la date d'expiration actuelle
          newExpirationDate.setTime(currentExpiration.getTime() + (selectedPlan.days * 24 * 60 * 60 * 1000));
        } else {
          // Si l'annonce est expirée, ajouter les jours à partir d'aujourd'hui
          newExpirationDate.setDate(newExpirationDate.getDate() + selectedPlan.days);
        }
      } else {
        newExpirationDate.setDate(newExpirationDate.getDate() + selectedPlan.days);
      }

      // Enregistrer le paiement
      await addDoc(collection(db, 'announcement-payments'), {
        userId: user.uid,
        announcementId: announcement.id,
        amount: selectedPlan.price,
        planId: selectedPlan.id,
        planDuration: selectedPlan.days,
        paymentMethod: selectedMethod,
        paymentReference: paymentData?.paymentReference || 'direct-payment',
        type: 'renewal',
        status: 'completed',
        createdAt: serverTimestamp()
      });

      // Mettre à jour l'annonce
      const announcementRef = doc(db, 'announcements', announcement.id);
      await updateDoc(announcementRef, {
        'subscriptionPlan.expirationDate': newExpirationDate,
        'subscriptionPlan.renewedAt': serverTimestamp(),
        'subscriptionPlan.planId': selectedPlan.id,
        'subscriptionPlan.duration': selectedPlan.days,
        'subscriptionPlan.price': selectedPlan.price,
        status: 'active'
      });

      // Créer une notification pour la nouvelle expiration
      await addDoc(collection(db, 'notifications'), {
        userId: user.uid,
        announcementId: announcement.id,
        type: 'expiration_warning',
        message: `Votre annonce "${announcement.titre}" va expirer le ${newExpirationDate.toLocaleDateString()}`,
        status: 'pending',
        scheduledFor: new Date(newExpirationDate.getTime() - (24 * 60 * 60 * 1000)),
        createdAt: serverTimestamp()
      });

      setSuccessModalVisible(true);
    } catch (error) {
      console.error('Error renewing announcement:', error);
      showAlert('Erreur', 'Une erreur est survenue lors du renouvellement');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Erreur de paiement:', error);
    showAlert('Erreur', 'Une erreur est survenue lors du paiement. Veuillez réessayer.');
    setLoading(false);
  };

  const handleRenewal = () => {
    if (!selectedPlan) {
      showAlert('Erreur', 'Veuillez sélectionner un forfait');
      return;
    }

    if (!selectedMethod) {
      showAlert('Erreur', 'Veuillez sélectionner une méthode de paiement');
      return;
    }

    // Activer le formulaire de paiement Campay
    setShowCampayForm(true);
  };

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.background }]}>
      <StatusBar barStyle={theme.statusBar} backgroundColor={theme.background} />
      
      <View style={styles.header}>
        <TouchableOpacity 
          style={[styles.backButton, { backgroundColor: theme.surface }]}
          onPress={() => {
            if (showCampayForm) {
              setShowCampayForm(false);
            } else {
              navigation.goBack();
            }
          }}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.text }]}>
          {showCampayForm 
            ? `Paiement via ${selectedMethod === 'mtn' ? 'Mobile Money' : 'Orange Money'}`
            : 'Renouveler l\'annonce'}
        </Text>
      </View>

      <ScrollView style={styles.content}>
        {!showCampayForm ? (
          <>
            <View style={styles.announcementInfo}>
              <Text style={[styles.announcementTitle, { color: theme.text }]}>
                {announcement.titre}
              </Text>
              {announcement.subscriptionPlan?.expirationDate && (
                <Text style={[styles.expirationInfo, { color: theme.textSecondary }]}>
                  Expire le: {new Date(announcement.subscriptionPlan.expirationDate).toLocaleDateString('fr-FR')}
                </Text>
              )}
            </View>

            <View style={styles.plansContainer}>
              <Text style={[styles.sectionTitle, { color: theme.text }]}>
                Choisir un forfait
              </Text>
              {SUBSCRIPTION_PLANS.map((plan) => (
                <TouchableOpacity
                  key={plan.id}
                  style={[
                    styles.planCard,
                    { 
                      backgroundColor: theme.surface,
                      borderColor: selectedPlan?.id === plan.id ? plan.color : theme.border
                    }
                  ]}
                  onPress={() => setSelectedPlan(plan)}
                >
                  <View style={styles.planHeader}>
                    <Text style={[styles.planName, { color: theme.text }]}>
                      {plan.name}
                    </Text>
                    <Text style={[styles.planPrice, { color: plan.color }]}>
                      {plan.price.toLocaleString()} XAF
                    </Text>
                  </View>
                  <Text style={[styles.planDuration, { color: theme.textSecondary }]}>
                    Durée: {plan.days} jours
                  </Text>
                  {selectedPlan?.id === plan.id && (
                    <MaterialCommunityIcons 
                      name="check-circle" 
                      size={24} 
                      color={plan.color} 
                      style={styles.checkIcon}
                    />
                  )}
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.paymentSection}>
              <Text style={[styles.sectionTitle, { color: theme.text }]}>
                Mode de paiement
              </Text>
              {paymentMethods.map((method) => (
                <TouchableOpacity
                  key={method.id}
                  style={[
                    styles.methodCard,
                    { 
                      backgroundColor: theme.surface,
                      borderColor: selectedMethod === method.id ? method.color : theme.border
                    }
                  ]}
                  onPress={() => setSelectedMethod(method.id)}
                >
                  <View style={[styles.methodIcon, { backgroundColor: method.color + '20' }]}>
                    <MaterialCommunityIcons name={method.icon} size={30} color={method.color} />
                  </View>
                  <Text style={[styles.methodName, { color: theme.text }]}>
                    {method.name}
                  </Text>
                  {selectedMethod === method.id && (
                    <MaterialCommunityIcons 
                      name="check-circle" 
                      size={24} 
                      color={method.color}
                    />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </>
        ) : (
          <CampayPaymentForm 
            amount={selectedPlan.price}
            document={{
              id: announcement.id,
              type: 'renewal',
              titre: announcement.titre || 'Annonce'
            }}
            documentDetail={{
              id: 'renewal-' + selectedPlan.id,
              titre: `Renouvellement ${selectedPlan.name}`,
              prix: selectedPlan.price
            }}
            userId={user.uid}
            onSuccess={handleRenewalSuccess}
            onError={handlePaymentError}
            theme={theme}
            paymentMethod={selectedMethod}
            navigation={navigation}
          />
        )}
      </ScrollView>
      

      {!showCampayForm && (
        <View style={[styles.bottomBar, { backgroundColor: theme.surface }]}>
          <TouchableOpacity
            style={[
              styles.renewButton,
              { opacity: !selectedPlan || !selectedMethod || loading ? 0.5 : 1 }
            ]}
            onPress={handleRenewal}
            disabled={!selectedPlan || !selectedMethod || loading}
          >
            <LinearGradient
              colors={[theme.primary, theme.primaryDark || theme.primary]}
              style={styles.renewButtonGradient}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.renewButtonText}>
                  Continuer
                </Text>
              )}
            </LinearGradient>
          </TouchableOpacity>
        </View>
      )}
<View style={[styles.bottomSpace, {flex: 1}, { backgroundColor: theme.background }]} />
      <Modal
        visible={successModalVisible}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.surface }]}>
            <View style={[styles.successIcon, { backgroundColor: theme.primary + '20' }]}>
              <MaterialCommunityIcons name="check-circle" size={50} color={theme.primary} />
            </View>
            <Text style={[styles.modalTitle, { color: theme.text }]}>
              Renouvellement réussi !
            </Text>
            <Text style={[styles.modalMessage, { color: theme.textSecondary }]}>
              Votre annonce a été renouvelée pour {selectedPlan?.days} jours supplémentaires
            </Text>
            <TouchableOpacity
              style={[styles.modalButton, { backgroundColor: theme.primary }]}
              onPress={() => {
                setSuccessModalVisible(false);
                navigation.goBack();
              }}
            >
              <Text style={styles.modalButtonText}>Retour aux annonces</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  backButton: {
    padding: 8,
    borderRadius: 8,
    marginRight: 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  announcementInfo: {
    marginBottom: 24,
  },
  announcementTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  expirationInfo: {
    fontSize: 14,
  },
  plansContainer: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  planCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 2,
    marginBottom: 12,
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  planName: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  planPrice: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  planDuration: {
    fontSize: 14,
  },
  checkIcon: {
    position: 'absolute',
    right: 16,
    top: 16,
  },
  paymentSection: {
    marginBottom: 24,
  },
  methodCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    borderWidth: 2,
    marginBottom: 12,
  },
  methodIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  methodName: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
  },
  bottomBar: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
  },
  renewButton: {
    overflow: 'hidden',
    borderRadius: 12,
  },
  renewButtonGradient: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  renewButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '80%',
    padding: 24,
    borderRadius: 20,
    alignItems: 'center',
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  modalButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});